import { DATETIME_FORMAT_WITH_SECONDS, NO_DATA } from "@/constants";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { EnumWithValueFilter } from "@/components/data-table/components/EnumWithValueFilter";
import { DBOperation } from "@/lib/dbOperation";
import { tableNames } from "@/lib/tableNames";
import { toSnakeCase } from "@/lib/utils";
import { formatDateTime } from "@/lib/utils/formatters";
import { ReadAuditLog } from "@/types/auditLog";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type AuditLogsColumnOverrides = Record<string, ColumnDef<ReadAuditLog>>;

export const useAuditLogsColumnOverrides = () => {
  const { t } = useTranslation();

  const columnOverrides = useMemo(
    (): AuditLogsColumnOverrides => ({
      item_identifier: {
        id: "item_identifier",
        cell: ({ row }) => row.original.item_identifier ?? NO_DATA,
      },
      changed_at: {
        id: "changed_at",
        enableColumnFilter: false,
        cell: ({ row }) =>
          formatDateTime(row.original.changed_at, DATETIME_FORMAT_WITH_SECONDS),
      },
      operation: {
        id: "operation",
        cell: ({ row }) => t(`auditLogs.dbOperation.${row.original.operation}`),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: DBOperation,
              translationKey: "auditLogs.dbOperation",
            },
          },
        },
      },
      table_name: {
        id: "table_name",
        enableSorting: false,
        cell: ({ row }) => (
          <span>
            {t(`auditLogs.tableName.${toSnakeCase(row.original.table_name)}`)}
            <span className="text-muted-foreground text-xs ml-1">
              ({row.original.table_name})
            </span>
          </span>
        ),
        meta: {
          filter: {
            component: EnumWithValueFilter,
            props: {
              options: tableNames
                .map(toSnakeCase)
                .sort((a, b) =>
                  t(`auditLogs.tableName.${a}`).localeCompare(
                    t(`auditLogs.tableName.${b}`),
                  ),
                ),
              translationKey: "auditLogs.tableName",
            },
          },
        },
      },
    }),
    [t],
  );

  return columnOverrides;
};
